import { getServiceName } from '@repo/utils';
import { useQuery } from '@tanstack/react-query';

import http from 'services/http';
import { checkIsLogin } from 'utils/auth';

/**
 * 샛별 신규 애셋 달성 보너스 응답 타입과 조회 훅 (studio#2524).
 *
 * BE 문서「샛별 달성 보너스 API 가이드」(affiliate #64 · MR !252, 10/8 dev 배포) 기준.
 * 금액은 숫자로만 오고 표기는 FE 가 한다. 보너스·적용 구간·남은 금액·상태는 응답 값을 그대로 쓴다.
 * FE 에서 계산하면 정책 완료 기준 "어드민 값 = 리워드 페이지 값" 이 깨진다 (#2182 와 같은 원칙).
 */

export const RISING_STAR_BONUS_KEY = {
  all: ['risingStarBonus'],
  detail: () => [...RISING_STAR_BONUS_KEY.all, 'detail'],
};

/** 26일 산정 → CALCULATED, 말일 지급 → PAID, 1일 새 달 ESTIMATED. CANCELLED 는 지급일에 샛별이 아니어서 미지급으로 끝난 달 */
export type RisingStarBonusStatus = 'ESTIMATED' | 'CALCULATED' | 'PAID' | 'CANCELLED';
/** 고정 · 50% 올림 · 없음(미달성) */
export type RisingStarBonusRule = 'FIXED' | 'HALF_ROUND_UP' | 'NONE';
export type RisingStarAssetSourceType = 'persona' | 'universe' | 'scene';

export interface RisingStarBonusTier {
  threshold: number;
  rule: RisingStarBonusRule;
  /** 30,000 구간(HALF_ROUND_UP)은 고정 금액이 없어 null */
  bonus: number | null;
}

export interface RisingStarBonusAsset {
  sourceType: RisingStarAssetSourceType;
  sourceId: number;
  /** 산정 뒤에는 산정 때 이름으로 고정 */
  name: string;
  /** UTC ISO */
  firstPublishedAt: string;
  reward: number;
  /** 산정 후 삭제·비공개 등으로 합산에서 빠짐. ESTIMATED 는 공개 중인 애셋만 계산해 항상 false */
  isExcluded: boolean;
  excludedReason: 'DELETED' | 'UNPUBLISHED' | 'BLOCKED' | 'LINK_SHARED' | null;
  excludedAt: string | null;
}

export interface RisingStarBonusCurrent {
  status: RisingStarBonusStatus;
  /** 상단 금액 = 달성 보너스(어드민 수정값 반영) = calculation.finalBonus */
  amount: number;
  schedule: {
    /** 'YYYY-MM' — 어느 달 이야기인지는 항상 이 값으로 보여준다 */
    targetMonth: string;
    /** 산정 기준 시각 — UTC ISO (그 달 25일 23:59 KST) */
    asOfAt: string;
    periodStartAt: string;
    periodEndAt: string;
    /** ESTIMATED 면 이번 달 기준 시각, 그 뒤면 다음 달 기준 시각 */
    nextCalculationAt: string;
    /** 'YYYY-MM-DD' (산정 월 말일, KST) */
    payoutDate: string;
  };
  tiers: RisingStarBonusTier[];
  /** 미달성이면 null */
  currentTier: RisingStarBonusTier | null;
  /** 최고 구간이면 null */
  nextTier: { threshold: number; remaining: number; bonus: number | null } | null;
  calculation: {
    /** 제외 애셋 뺀 합산 리워드 */
    totalReward: number;
    /** 미달성이면 null */
    tierThreshold: number | null;
    rule: RisingStarBonusRule;
    calculatedBonus: number;
    overrideBonus: number | null;
    finalBonus: number;
  };
  assets: {
    /** 제외 뺀 대상 수 */
    targetCount: number;
    /** 제외 포함 전체 수 */
    totalCount: number;
    /** 제외 뺀 합계 = calculation.totalReward */
    totalReward: number;
    /** 리워드 많은 순 */
    items: RisingStarBonusAsset[];
  };
}

export interface RisingStarBonusHistory {
  /** 'YYYY-MM' */
  targetMonth: string;
  /** 'YYYY-MM-DD' */
  payoutDate: string;
  /** 그 달(KST 1일~말일) 메시지·트리거 이미지·샴푸 리워드 합 */
  basicReward: number;
  /** 미달성이면 0 */
  bonus: number;
  /** 지급 때 구간. 미달성이면 null */
  tierThreshold: number | null;
  rule: RisingStarBonusRule;
  /** 회수 합계(0 이하). 없으면 0. 드물게 소수 */
  clawback: number;
  /** basicReward + bonus + clawback. 드물게 소수 */
  monthTotal: number;
}

/**
 * 모든 크리에이터에게 200. 블록을 그릴지는 null 여부로만 정한다.
 * - 샛별: current 객체 + history 배열(없으면 [])
 * - 샛별 시절 보너스를 받은 별지기: current null + history 배열
 * - 그 외: 둘 다 null
 */
export interface RisingStarBonusResponseDto {
  current: RisingStarBonusCurrent | null;
  /** 지급된 산정 월만, 최신순 */
  history: RisingStarBonusHistory[] | null;
}

/** 대상은 버블챗만 (정책 v1.2 "버블챗 한국만"). 대시보드는 bubblechat-reward·tingle-reward 가 공유한다 */
const BONUS_SERVICE_NAME = 'bubblechat';

export const useGetRisingStarBonus = () => {
  return useQuery({
    queryKey: RISING_STAR_BONUS_KEY.detail(),
    queryFn: () =>
      // 실패해도 토스트를 띄우지 않는다 — 운영 배포 전에는 404 가 정상이고, 섹션만 조용히 감춘다.
      http.get<RisingStarBonusResponseDto>('/affiliate/v1/revenues/rising-star-bonus', {}, { useErrorToast: false }),
    // 등급으로 거르지 않는다 — 미지급(CANCELLED)은 '지급일에 샛별이 아닌' 분에게 오는 상태라 등급으로 거르면 볼 수 없다
    // (10/23 기획 답변 A안). 노출은 응답의 null 여부로만 정한다 (BE 가이드: 모든 크리에이터 200).
    enabled: checkIsLogin() && getServiceName() === BONUS_SERVICE_NAME,
    // 전역 throwOnError: true 를 끈다. 켜두면 대시보드 최상단이 에러 패널로 덮인다.
    throwOnError: false,
    staleTime: 1000 * 10,
    // ESTIMATED 는 호출마다 서버가 새로 계산한다 — BE 요청대로 화면 진입 때 한 번만 부르고 포커스 재요청은 하지 않는다.
    refetchOnWindowFocus: false,
    // 재연결도 재계산을 부른다 — 같은 이유로 끈다
    refetchOnReconnect: false,
  });
};
